// The console frame: sidebar nav on md+ screens; on phones a top bar with a
// menu button that opens the same nav as a drawer.

import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import { cn } from '@/lib/format'
import { useAttentionQueue } from '@/lib/useAttentionQueue'
import { AuditIcon, DashboardIcon, LogOutIcon, MenuIcon, TenantsIcon, UsersIcon, XIcon } from '@/components/ui/icons'

const NAV = [
  { to: '/dashboard', label: 'Dashboard', Icon: DashboardIcon, badge: 'attention' as const },
  { to: '/tenants', label: 'Tenants', Icon: TenantsIcon },
  { to: '/users', label: 'Users', Icon: UsersIcon },
  { to: '/audit', label: 'Audit', Icon: AuditIcon },
]

/** Needs-attention count for the Dashboard nav badge (the same queue the Dashboard shows). */
function useAttentionCount(): number | null {
  const { items, status } = useAttentionQueue()
  return status === 'success' ? items.length : null
}

export function Brand({ className }: { className?: string }) {
  return (
    <Link to="/dashboard" className={cn('font-display text-xl font-extrabold uppercase leading-none tracking-[0.03em] text-ink', className)}>
      Score<span className="text-accent">LM</span> <span className="text-ink">Ops</span>
    </Link>
  )
}

function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const attention = useAttentionCount()
  return (
    <ul className="grid gap-0.5">
      {NAV.map(({ to, label, Icon, badge }) => (
        <li key={to}>
          <NavLink
            to={to}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13.5px] font-semibold transition-colors',
                isActive ? 'bg-hover text-ink' : 'text-muted hover:bg-hover/50 hover:text-ink',
              )
            }
          >
            {({ isActive }) => (
              <>
                <Icon size={17} className={isActive ? 'text-accent' : 'text-subtle'} />
                <span className="flex-1">{label}</span>
                {badge === 'attention' && attention ? (
                  <span
                    className="rounded-full bg-accent-soft px-1.5 py-px font-mono text-2xs font-semibold text-accent-ink"
                    aria-label={`${attention} need attention`}
                  >
                    {attention}
                  </span>
                ) : null}
              </>
            )}
          </NavLink>
        </li>
      ))}
    </ul>
  )
}

function Account() {
  const { user, logout } = useAuth()
  if (!user) return null
  return (
    <div className="grid gap-2 border-t border-line pt-3">
      <div className="min-w-0 px-2.5">
        <p className="truncate text-[13px] font-semibold text-ink">{user.name || 'Super-admin'}</p>
        <p className="truncate font-mono text-2xs text-muted">{user.email}</p>
      </div>
      <button
        type="button"
        onClick={() => logout('signed_out')}
        className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] font-semibold text-muted transition-colors hover:bg-hover/50 hover:text-ink"
      >
        <LogOutIcon size={16} className="text-subtle" />
        Sign out
      </button>
    </div>
  )
}

function Sidebar() {
  return (
    <aside className="sticky top-0 flex h-screen flex-col px-3 py-5">
      <Brand className="px-2.5 pb-6" />
      <nav aria-label="Main" className="flex-1">
        <NavList />
      </nav>
      <Account />
    </aside>
  )
}

function MobileNav() {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  // Close on navigation.
  useEffect(() => setOpen(false), [location.pathname])

  useEffect(() => {
    if (!open) return
    panelRef.current?.querySelector<HTMLElement>('a, button')?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const button = buttonRef.current
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
      button?.focus()
    }
  }, [open])

  return (
    <>
      <header
        className="sticky top-0 z-30 flex items-center justify-between border-b border-line bg-sidebar/95 px-4 py-3 backdrop-blur md:hidden"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <Brand />
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          aria-expanded={open}
          aria-controls="mobile-nav"
          className="-mr-2 rounded-md p-2 text-muted hover:bg-hover hover:text-ink"
        >
          <MenuIcon size={20} />
        </button>
      </header>
      {open ? (
        <div className="fixed inset-0 z-40 md:hidden">
          <div aria-hidden className="absolute inset-0 animate-fade-in bg-black/60" onClick={() => setOpen(false)} />
          <div
            id="mobile-nav"
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] animate-drawer-in flex-col border-r border-line bg-sidebar px-3 pb-5"
            style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
          >
            <div className="flex items-center justify-between px-2.5 pb-5">
              <Brand />
              <button type="button" onClick={() => setOpen(false)} aria-label="Close menu" className="-mr-1.5 rounded-md p-1.5 text-muted hover:bg-hover hover:text-ink">
                <XIcon size={18} />
              </button>
            </div>
            <nav aria-label="Main" className="flex-1">
              <NavList onNavigate={() => setOpen(false)} />
            </nav>
            <Account />
          </div>
        </div>
      ) : null}
    </>
  )
}

export function AppShell() {
  return (
    <div className="min-h-screen md:grid md:grid-cols-[13.5rem_minmax(0,1fr)]">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-raised focus:px-3 focus:py-2 focus:text-ink">
        Skip to content
      </a>
      {/* The column carries the rail's colour, so it runs the full page height
          while the nav itself stays pinned (sticky) as you scroll. */}
      <div className="hidden border-r border-line bg-sidebar md:block">
        <Sidebar />
      </div>
      <div className="min-w-0">
        <MobileNav />
        <main id="main" tabIndex={-1} className="mx-auto w-full max-w-page px-4 pb-16 pt-5 focus:outline-none sm:px-6 md:px-8 md:pt-7">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
